import { useEffect, useRef, useState } from "react";
import { Trophy, Skull, X, Download, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { fmtChips } from "../../lib/api";

export const ordinal = (n) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;

const Buttons = ({ onLobby, onPlayAgain, size = "md" }) => {
  const cls = size === "md" ? "px-6 py-3 text-sm" : "px-4 py-2 text-xs";
  return (
    <div className="flex gap-3 justify-center">
      <button data-testid="sng-back-to-lobby" onClick={onLobby} className={`rounded-full font-display tracking-wider bg-white/10 hover:bg-white/20 btn-lift ${cls}`}>BACK TO LOBBY</button>
      <button data-testid="sng-play-again" onClick={onPlayAgain} className={`rounded-full font-display tracking-wider btn-gold btn-lift ${cls}`}>PLAY AGAIN</button>
    </div>
  );
};

// Tournament moments layered over the table: bust ribbon (left), blinds-up ribbon (centre), prize pop-up / knocked-out banner.
export const SngEvents = ({ t, hand, myUserId, onLobby, onPlayAgain, ribbonsOnly = false }) => {
  const [busts, setBusts] = useState([]); // queue of {user_id,name,place}
  const [blinds, setBlinds] = useState(null); // {sb,bb} to announce
  const [pendingBlinds, setPendingBlinds] = useState(null);
  const [result, setResult] = useState(null); // {kind:"prize"|"out"|"mtt-prize"|"mtt-out", place, amount, instance}
  const [myOut, setMyOut] = useState(null);
  const card = useRef(null);
  const [saving, setSaving] = useState(false);
  // in the money: the result card as a PNG for the player's phone / computer (buttons left out)
  const savePicture = async () => {
    if (!card.current || saving) return;
    setSaving(true);
    try {
      const url = await toPng(card.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0b0b0f",
        filter: (n) => !(n.dataset && n.dataset.noShot !== undefined) });
      const a = document.createElement("a");
      a.href = url; a.download = `${(t.name || "tournament").replace(/[^a-z0-9]+/gi, "-")}-${ordinal(result.place)}-place.png`; a.click();
    } finally { setSaving(false); }
  };  // MTT: my result, shown once my bust ribbon has crossed
  const seen = useRef(new Set());
  const old = useRef(new Set((t.places || []).map((p) => p.user_id))); // already out when this tab opened: no ribbon replay
  const level = useRef(t.level);
  const instance = useRef(t.instance_id);

  useEffect(() => {
    if (instance.current !== t.instance_id) { instance.current = t.instance_id; seen.current = new Set(); level.current = t.level; setResult(null); setBusts([]); setBlinds(null); setPendingBlinds(null); }
  }, [t.instance_id, t.level]);

  // eliminations: announce each newly placed player (not the last hand — that gets the prize pop-up)
  useEffect(() => {
    const fresh = (t.places || []).filter((p) => !seen.current.has(p.user_id));
    if (!fresh.length) return;
    fresh.forEach((p) => seen.current.add(p.user_id));
    const news = fresh.filter((p) => !old.current.has(p.user_id));
    if (news.length) setBusts((q) => [...q, ...news]);
    const me = fresh.find((p) => p.user_id === myUserId && !old.current.has(p.user_id));
    if (me && ribbonsOnly && me.place > 1) {
      // MTT: my own splash once my bust ribbon has crossed — in the money shows the prize, otherwise "unlucky"
      const prize = (t.paid_places || []).find((pp) => pp.place === me.place);
      setMyOut({ kind: prize ? "mtt-prize" : "mtt-out", place: me.place, amount: prize?.amount || 0,
        field: t.entries || (t.places || []).length + (t.remaining || 0), instance: t.instance_id });
    }
    if (me && !ribbonsOnly && me.place > (t.prize_split?.length || 0)) setResult({ kind: "out", place: me.place, instance: t.instance_id });
  }, [t.places, t.remaining, t.prize_split, t.instance_id, myUserId]);
  useEffect(() => {
    if (!myOut) return;
    const id = setTimeout(() => { setResult(myOut); setMyOut(null); }, 4400);
    return () => clearTimeout(id);
  }, [myOut]);
  useEffect(() => {
    if (!busts.length) return;
    const id = setTimeout(() => setBusts((q) => q.slice(1)), 4200); // = bust-slide duration (in 0.4 s · hold 3 s · out 0.8 s)
    return () => clearTimeout(id);
  }, [busts]);

  // blinds up: shown once the current hand is over, before the next deal
  useEffect(() => {
    if (t.status === "RUNNING" && t.level > level.current) { level.current = t.level; setPendingBlinds({ sb: t.sb, bb: t.bb }); }
  }, [t.level, t.status, t.sb, t.bb]);
  useEffect(() => {
    if (pendingBlinds && !hand) { setBlinds(pendingBlinds); setPendingBlinds(null); }
  }, [pendingBlinds, hand]);
  useEffect(() => {
    if (!blinds) return;
    const id = setTimeout(() => setBlinds(null), 5500);
    return () => clearTimeout(id);
  }, [blinds]);

  // prizes
  useEffect(() => {
    if (ribbonsOnly || t.status !== "FINISHED") return;
    const mine = (t.payouts || []).find((p) => p.user_id === myUserId && p.amount > 0);
    if (mine) setResult({ kind: "prize", place: mine.place, amount: mine.amount, instance: t.instance_id });
  }, [t.status, t.payouts, t.instance_id, myUserId]);

  const bust = busts[0];
  return (
    <>
      {bust && (
        <div key={bust.user_id} data-testid="sng-bust-banner" className="absolute left-0 top-[44%] z-40 bust-slide pointer-events-none">
          <div className="flex items-center gap-3 bg-red-700/90 border-y border-r border-red-300/40 text-white pl-8 pr-10 py-3 rounded-r-full shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
            <Skull className="w-5 h-5 text-red-200" />
            <span className="font-display tracking-wider text-base"><span className="text-amber-200">{bust.name}</span> busted in {ordinal(bust.place)} position</span>
          </div>
        </div>
      )}
      {blinds && (
        <div data-testid="sng-blinds-ribbon" className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-40 ribbon-in pointer-events-none">
          <div className="mx-auto w-[70%] bg-black/55 backdrop-blur-sm border-y-2 border-amber-400/70 py-4 text-center">
            <div className="text-[11px] uppercase tracking-[0.5em] text-amber-200/80">Level {t.level}</div>
            <div className="font-display text-2xl md:text-3xl tracking-wider text-amber-100">BLINDS ARE NOW <span className="text-amber-300">{blinds.sb}/{blinds.bb}</span></div>
          </div>
        </div>
      )}
      {result?.kind === "prize" && (
        <div data-testid="sng-prize-popup" className="absolute inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass rounded-3xl border border-amber-400/50 px-12 py-10 text-center flex flex-col items-center gap-4 rise shadow-[0_30px_90px_rgba(0,0,0,0.8)]">
            <div className="text-[11px] uppercase tracking-[0.5em] text-amber-300">Congratulations</div>
            <div className="font-display text-3xl md:text-4xl text-amber-100">You Won</div>
            <div className="flex items-center gap-3 text-xl font-display text-amber-200"><Trophy className="w-8 h-8 text-amber-400" /> {ordinal(result.place)} prize</div>
            <div data-testid="sng-prize-amount" className="font-mono text-4xl text-emerald-300">{fmtChips(result.amount)}</div>
            <div className="pt-2"><Buttons onLobby={onLobby} onPlayAgain={onPlayAgain} /></div>
          </div>
        </div>
      )}
      {(result?.kind === "mtt-prize" || result?.kind === "mtt-out") && (
        <div data-testid="mtt-result-splash" className="absolute inset-0 z-50 flex items-center justify-center bg-black/65">
          <div ref={card} className={`glass relative rounded-3xl border px-12 py-10 text-center flex flex-col items-center gap-3 rise shadow-[0_30px_90px_rgba(0,0,0,0.8)] ${result.kind === "mtt-prize" ? "border-amber-400/60" : "border-white/15"}`}>
            <button data-no-shot data-testid="mtt-result-close" onClick={() => setResult(null)} aria-label="Close" className="absolute top-3 right-3 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            {result.kind === "mtt-prize" ? (<>
              <div className="text-[11px] uppercase tracking-[0.5em] text-amber-300">Congratulations</div>
              <div className="font-display text-3xl md:text-4xl text-amber-100">You're in the money</div>
              <div className="flex items-center gap-3 text-xl font-display text-amber-200" data-testid="mtt-result-place"><Trophy className="w-8 h-8 text-amber-400" /> You finished {ordinal(result.place)}{result.field ? ` of ${result.field}` : ""}</div>
              <div data-testid="mtt-result-prize" className="font-mono text-4xl text-emerald-300">{fmtChips(result.amount)}</div>
              <div className="text-xs text-slate-400">Paid to your wallet when the tournament ends.</div>
            </>) : (<>
              <div className="text-[11px] uppercase tracking-[0.5em] text-slate-300">Unlucky this time</div>
              <div className="font-display text-3xl md:text-4xl text-amber-100">Maybe next time</div>
              <div className="text-lg text-slate-200" data-testid="mtt-result-place">You finished in <span className="font-display text-amber-200">{ordinal(result.place)} place</span>{result.field ? ` of ${result.field}` : ""}</div>
            </>)}
            {result.kind === "mtt-prize" && t.name && <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400" data-testid="mtt-result-event">{t.name}</div>}
            <div className="pt-3 flex items-center gap-3" data-no-shot>
              {result.kind === "mtt-prize" && (
                <button data-testid="mtt-result-save" onClick={savePicture} disabled={saving}
                  className="rounded-full px-5 py-2 text-xs bg-amber-400/15 border border-amber-300/40 text-amber-100 hover:bg-amber-400/25 btn-lift flex items-center gap-1.5">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Save picture</button>
              )}
              <button data-testid="mtt-result-watch" onClick={() => setResult(null)} className="rounded-full px-5 py-2 text-xs border border-white/20 text-slate-200 hover:bg-white/10 btn-lift">Keep watching</button>
              <button data-testid="mtt-result-lobby" onClick={onLobby} className="btn-gold rounded-full px-6 py-2 text-xs font-display btn-lift">Back to lobby</button>
            </div>
          </div>
        </div>
      )}
      {result?.kind === "out" && (
        <div data-testid="sng-out-banner" className="absolute left-1/2 bottom-6 -translate-x-1/2 z-50 rise">
          <div className="glass rounded-2xl border border-white/15 px-6 py-3 flex items-center gap-5">
            <div className="text-sm text-slate-200">You finished <span className="text-amber-200 font-display">{ordinal(result.place)}</span> — outside the prizes this time.</div>
            <Buttons onLobby={onLobby} onPlayAgain={onPlayAgain} size="sm" />
          </div>
        </div>
      )}
    </>
  );
};
